import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './chartLabels.css';
import '../ChartOptions.css';
import { ColorResult, SketchPicker } from 'react-color';
import SliderWithInput from '../SliderWithInput';
import {
  FormControl,
  MenuItem,
  Select,
  Typography,
  Popover,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TreeMapLabelOptions from './TreeMapLabelOptions';
import SnakeyLabelOptions from './SnakeyLabelOptions';
import {
  updateLabelOption,
  updateLabelPadding,
  updateLabelPosition,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { RootState } from '../../../redux';
import { ChartConLabelOptions } from '../../../redux/ChartPoperties/ChartControlsInterface';
import LabelFormatting from '../Format/LabelFormatting';
import SwitchWithInput from '../SwitchWithInput';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';

interface ChartLabelsProps {
  iconColor?: string;
  backgroundColor?: string;
}

const ChartLabels = ({ iconColor, backgroundColor }: ChartLabelsProps) => {
  const { chartControls, tabTileProps, chartProperties } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    chartProperties: state.chartProperties,
  }));
  const dispatch = useDispatch();
  const propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  const chartType = chartProperties.properties[propKey]?.chartType;

  const allThemes = useCombinedColorSchemes();
  const currentColorScheme = chartControls.properties[propKey].colorScheme;
  const selectedTheme =
    allThemes.find((theme: any) => theme.name === currentColorScheme) || allThemes[0];
  const dynamicColor = selectedTheme.dark ? '#ffffff' : '#757474';

  const [isColorPopoverOpen, setColorPopOverOpen] = useState<boolean>(false);
  const [isBgColorPopoverOpen, setBgColorPopOverOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [bgAnchorEl, setBgAnchorEl] = useState<HTMLDivElement | null>(null);

  const showLabel: boolean = chartControls.properties[propKey].labelOptions.showLabel;
  const showState: boolean = chartControls.properties[propKey].labelOptions.showStateLabel;
  const stateCheckBox: boolean = chartControls.properties[propKey].labelOptions.stateCheckBox;
  var labelOptions: ChartConLabelOptions = chartControls.properties[propKey].labelOptions;

  const labelPositionOptions = [
    { name: 'Outside', value: 'outside' },
    { name: 'Inside', value: 'inside' },
  ];

  const labelCityList: any[] = [
    { name: 'show', value: true },
    { name: 'hide', value: false },
  ];

  const labelOptionsList = [
    { name: 'Show', value: true },
    { name: 'Hide', value: false },
  ];

  const geoLocation = chartProperties.properties[propKey].Geo.geoLocation;

  const isBubbleCity =
    chartProperties.properties[propKey].chartType === 'bubbleMap' && geoLocation.includes('Cities');

  const isSwitchChecked = isBubbleCity ? showState : showLabel;

  const handleLabelToggle = () => {
    dispatch(updateLabelOption(propKey, 'showLabel', !showLabel));
  };

  const handleLabelBackgroundToggle = () => {
    if (!labelOptions.labelBackground && !labelOptions.labelBackgroundColor) {
      dispatch(updateLabelOption(propKey, 'labelBackgroundColor', '#E9E9E9'));
    }
    dispatch(updateLabelOption(propKey, 'labelBackground', !labelOptions.labelBackground));
  };

  const renderLabels = () => {
    return labelOptionsList.map((item, i) => {
      const isSelected = item.value === showLabel;

      return (
        <button
          onClick={() => {
            dispatch(updateLabelOption(propKey, 'showLabel', item.value));
          }}
          className={isSelected ? 'radioButtonSelected' : 'radioButton'}
          key={i}
          style={{
            backgroundColor: isSelected ? 'rgba(224, 224, 224, 1)' : 'white',
            cursor: isSelected ? 'auto' : 'pointer',
            fontWeight: isSelected ? '600' : 'normal',
          }}
        >
          {item.name}
        </button>
      );
    });
  };

  const renderCityLabels = () => {
    return labelCityList.map((item: any, i: number) => {
      const isSelected = item.value === showState;
      return (
        <button
          onClick={() => {
            dispatch(updateLabelOption(propKey, 'showStateLabel', !showState));
          }}
          className={isSelected ? 'radioButtonSelected' : 'radioButton'}
          key={i}
          style={{
            backgroundColor: isSelected ? 'rgba(224, 224, 224, 1)' : 'white',
            cursor: isSelected ? 'auto' : 'pointer',
            fontWeight: isSelected ? '600' : 'normal',
          }}
        >
          {item.name}
        </button>
      );
    });
  };

  const handleStateLabel = () => {
    dispatch(updateLabelOption(propKey, 'showLabel', !showLabel));
  };

  const stateHandle = () => {
    return (
      <div>
        <div style={{ marginLeft: '-30px', marginTop: '5px' }}>
          {/* <SwitchWithInput isChecked={showLabel} onSwitch={handleStateLabel} /> */}
        </div>
        <div className="radioButtons" style={{ marginTop: '15px' }}>
          {renderLabels()}
        </div>
      </div>
    );
  };

  const handleColorClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setColorPopOverOpen(!isColorPopoverOpen);
  };

  const handleColorClose = () => {
    setColorPopOverOpen(false);
    setAnchorEl(null);
  };

  const handleBgColorClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setBgAnchorEl(event.currentTarget);
    setBgColorPopOverOpen(!isBgColorPopoverOpen);
  };

  const handleBgColorClose = () => {
    setBgColorPopOverOpen(false);
    setBgAnchorEl(null);
  };

  const checkBox = () => {
    return (
      <div style={{ display: 'flex', paddingLeft: '4px ', marginTop: '10px', marginBottom: '4px' }}>
        {showLabel === true && (
          <>
            <input
              type="checkbox"
              checked={stateCheckBox}
              onChange={(e) =>
                dispatch(updateLabelOption(propKey, 'stateCheckBox', e.target.checked))
              }
            />
            <Typography
              sx={{
                paddingLeft: '2px',
                marginBottom: '0px',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Only the Data Points
            </Typography>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="optionsInfo" style={{ overflowX: 'hidden' }}>
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: '0.15rem',
          marginTop: '0',
          marginBottom: '0px',
        }}
      >
        <label
          className="enableDisableLabel"
          style={{
            fontWeight: 600,
            color: iconColor ? iconColor : '#404040',
          }}
        >
          LABEL
        </label>
        {chartProperties.properties[propKey].chartType !== 'bubbleMap' &&
          chartProperties.properties[propKey].chartType !== 'filledMap' && (
            <Box
              sx={{
                width: '50px',
                height: '24px',
                display: 'flex',
                transform: 'translateX(63px)',
                marginBottom: '0px',

                '@supports (-moz-appearance: none)': {
                  transform: 'translateX(56px)',
                },
              }}
            >
              <SwitchWithInput isChecked={isSwitchChecked} onSwitch={handleLabelToggle} />
            </Box>
          )}
      </div>
      {chartProperties.properties[propKey].chartType === 'bubbleMap' &&
        geoLocation.includes('Cities') && (
          <div>
            <div style={{ textAlign: 'left' }}>
              <Typography
                sx={{
                  paddingLeft: '8px',
                  fontSize: '0.875rem',
                  color: iconColor ? iconColor : '#484848',
                  fontWeight: 600,
                  margin: 0,
                  marginTop: '5px',
                }}
              >
                {' '}
                City
              </Typography>
            </div>

            <div>
              <div className="radioButtons" style={{ marginTop: '15px' }}>
                {renderCityLabels()}
              </div>
              {/* <div style={{ textAlign: 'left' }}>{checkBox()}</div> */}
            </div>

            <div style={{ textAlign: 'left' }}>
              <Typography
                sx={{
                  paddingLeft: '8px',
                  fontSize: '0.875rem',
                  color: iconColor ? iconColor : '#484848',
                  fontWeight: 600,
                  margin: 0,
                  marginTop: '5px',
                }}
              >
                {' '}
                Base Map Label
              </Typography>
              {stateHandle()}
              {checkBox()}
            </div>
          </div>
        )}

      {(chartProperties.properties[propKey].chartType === 'bubbleMap' ||
        chartProperties.properties[propKey].chartType === 'filledMap') &&
        !geoLocation.includes('Cities') && (
          <div>
            {stateHandle()}
            {checkBox()}
          </div>
        )}

      {showLabel === true && (
        <>
          <div
            style={{
              display: 'flex',
              paddingBottom: '8px',
              flexDirection: 'column',
            }}
          >
            {(chartProperties.properties[propKey].chartType === 'pie' ||
              chartProperties.properties[propKey].chartType === 'donut' ||
              chartProperties.properties[propKey].chartType === 'rose') && (
              <>
                <div
                  className="optionDescription"
                  style={{ color: iconColor ? iconColor : '#404040' }}
                >
                  Label Position:
                </div>
                <FormControl
                  fullWidth
                  size="small"
                  style={{ fontSize: '12px', borderRadius: '4px', paddingLeft: '4px' }}
                >
                  <Select
                    value={labelOptions.pieLabel.labelPosition}
                    variant="outlined"
                    onChange={(e) => dispatch(updateLabelPosition(propKey, e.target.value))}
                    sx={{
                      fontSize: '12px',
                      width: '99%',
                      margin: '0 auto 0.5rem auto',
                      backgroundColor: 'white',
                      height: '1.5rem',
                      color: '#404040',
                      textAlign: 'left',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2bb9bb' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root.Mui-selected': {
                            backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                          },
                          '& .MuiMenuItem-root.Mui-selected:hover': {
                            backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                          },
                        },
                      },
                    }}
                  >
                    {labelPositionOptions.map((position) => (
                      <MenuItem
                        key={position.name}
                        value={position.value}
                        sx={{ padding: '2px 10px', fontSize: '12px' }}
                      >
                        {position.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {labelOptions.pieLabel.labelPosition === 'outside' && (
                  <div
                    style={{
                      // Disable interaction and lower opacity when background is true
                      opacity: labelOptions.labelBackground ? 0.5 : 1,
                      pointerEvents: labelOptions.labelBackground ? 'none' : 'auto',
                    }}
                  >
                    <div
                      className="optionDescription"
                      style={{
                        marginTop: '0',
                        color: iconColor ? iconColor : '#404040',
                      }}
                    >
                      Label Padding:
                    </div>
                    <SliderWithInput
                      percent={true}
                      sliderValue={labelOptions.pieLabel.labelPadding}
                      sliderMinMax={{ min: 0, max: 40, step: 1 }}
                      changeValue={(value: number) => dispatch(updateLabelPadding(propKey, value))}
                    />
                  </div>
                )}
              </>
            )}

            <div>
              <div
                className="optionDescription"
                style={{
                  marginTop: '2px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Label Size
              </div>
              <SliderWithInput
                percent={false}
                sliderValue={labelOptions.fontSize}
                sliderMinMax={{ min: 8, max: 50, step: 1 }}
                changeValue={(value: number) =>
                  dispatch(updateLabelOption(propKey, 'fontSize', value))
                }
              />
              <div>
                <div
                  className="optionDescription"
                  style={{
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: iconColor ? iconColor : '#404040',
                  }}
                >
                  Label Color
                  {labelOptions.labelColor && labelOptions.labelColor !== '' && (
                    <Tooltip
                      title="Reset to default color"
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => dispatch(updateLabelOption(propKey, 'labelColor', ''))}
                        sx={{
                          padding: '2px',
                          marginLeft: '5px',
                          color: iconColor || 'gray',
                          '&:hover': {
                            color: iconColor || '#333',
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        <RestartAltIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px 8px' }}>
                  <div
                    style={{
                      height: '1.25rem',
                      width: '25%',
                      backgroundColor: labelOptions.labelColor || dynamicColor,
                      border: '2px solid darkgray',
                      cursor: 'pointer',
                    }}
                    onClick={handleColorClick}
                  ></div>
                </div>
              </div>
            </div>
            <div
              style={{
                borderTop: '1px solid rgb(211,211,211)',
                margin: '0.8rem 2% 0.6rem',
              }}
            ></div>
            <div>
              <div
                className="optionDescription"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingLeft: '0.15rem',
                  marginTop: '0',
                  marginBottom: '0px',
                }}
              >
                <label
                  className="enableDisableLabel"
                  style={{
                    marginRight: '10px',
                    color: iconColor ? iconColor : '#404040',
                  }}
                >
                  Label Background
                </label>

                <div
                  style={{
                    width: '50px',
                    height: '24px',
                    display: 'flex',
                    transform: 'translateX(63px)',
                    marginBottom: '7px',
                  }}
                >
                  <SwitchWithInput
                    isChecked={labelOptions.labelBackground ? labelOptions.labelBackground : false}
                    onSwitch={handleLabelBackgroundToggle}
                  />
                </div>
              </div>
              {labelOptions.labelBackground && (
                <div>
                  <div
                    className="optionDescription"
                    style={{
                      marginTop: '-6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      color: iconColor ? iconColor : '#404040',
                    }}
                  >
                    Label Background Color
                    {labelOptions.labelBackgroundColor &&
                      labelOptions.labelBackgroundColor !== '#E9E9E9' && (
                        <Tooltip
                          title="Reset to default color"
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '12px',
                              },
                            },
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              dispatch(
                                updateLabelOption(propKey, 'labelBackgroundColor', '#E9E9E9'),
                              )
                            }
                            sx={{
                              padding: '2px',
                              marginLeft: '5px',
                              color: 'gray',
                              '&:hover': {
                                color: '#333',
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            <RestartAltIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px 8px' }}>
                    <div
                      style={{
                        height: '1.25rem',
                        width: '25%',
                        backgroundColor: labelOptions.labelBackgroundColor || 'white',
                        border: '2px solid darkgray',
                        cursor: 'pointer',
                      }}
                      onClick={handleBgColorClick}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {chartProperties.properties[propKey].chartType === 'treeMap' && (
              <>
                <div
                  style={{ borderTop: '1px solid rgb(211,211,211)', margin: '1.5rem 1% 1rem' }}
                ></div>
                <TreeMapLabelOptions iconColor={iconColor} />
              </>
            )}

            {chartProperties.properties[propKey].chartType === 'sankey' && (
              <>
                <div
                  style={{ borderTop: '1px solid rgb(211,211,211)', margin: '1.5rem 1% 1rem' }}
                ></div>
                <SnakeyLabelOptions iconColor={iconColor} backgroundColor={backgroundColor} />
              </>
            )}
            {chartType !== 'bubbleMap' &&
              chartType !== 'filledMap' &&
              chartType !== 'boxPlot' &&
              chartType !== 'funnel' &&
              chartType !== 'gauge' && (
                <LabelFormatting
                  formatFor="label"
                  iconColor={iconColor}
                  backgroundColor={backgroundColor}
                />
              )}
          </div>
        </>
      )}

      <Popover
        open={isColorPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleColorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            boxShadow: 'none',
            border: '1px solid #ddd',
            borderRadius: '6px',
          },
        }}
      >
        <div style={{ padding: '0' }}>
          <SketchPicker
            color={labelOptions.labelColor}
            width="13rem"
            onChange={(color: ColorResult) =>
              dispatch(updateLabelOption(propKey, 'labelColor', color.hex))
            }
            disableAlpha
            styles={{
              default: {
                picker: {
                  boxShadow: 'none',
                },
              },
            }}
          />
        </div>
      </Popover>

      <Popover
        open={isBgColorPopoverOpen}
        anchorEl={bgAnchorEl}
        onClose={handleBgColorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            boxShadow: 'none',
            border: '1px solid #ddd',
            borderRadius: '6px',
            '& .sketch-picker ': {
              boxShadow: 'none !important',
            },
          },
        }}
      >
        <div style={{ padding: '0' }}>
          <SketchPicker
            color={labelOptions.labelBackgroundColor || 'white'}
            width="13rem"
            onChange={(color: ColorResult) => {
              dispatch(updateLabelOption(propKey, 'labelBackgroundColor', color.hex));
            }}
            disableAlpha
            styles={{
              default: {
                picker: {
                  boxShadow: 'none',
                },
              },
            }}
          />
        </div>
      </Popover>
    </div>
  );
};

export default ChartLabels;
